import { useState, useEffect } from "react";
import { CheckCompany, CheckSignatory, CheckResponseError, isValidEmail, isCheckError } from "winslow-core";
import { apiCheckCreateSignatory, apiCheckGetSignatories } from "../../../services/PayrollService";
import { Button, Label } from "../../../ds2/components";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";

type AppPaySignatoryNewProps = {
    checkCompany: CheckCompany | null;
    onSignatoryCreated?: (signatories: CheckSignatory[]) => void;
    onError?: (error: CheckResponseError | undefined) => void;
};

export const AppPaySignatoryNew = ({ 
    checkCompany, 
    onSignatoryCreated,
    onError 
}: AppPaySignatoryNewProps) => {
    const [newSignatory, setNewSignatory] = useState<Omit<CheckSignatory, "id">>({
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        title: "",
    });

    const [isCreatingSignatory, setIsCreatingSignatory] = useState(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);
    const [isEmailInvalid, setIsEmailInvalid] = useState(false);

    // Validate email whenever it changes
    useEffect(() => {
        const trimmedEmail = newSignatory.email.trim();
        if (trimmedEmail.length > 0) {
            setIsEmailInvalid(!isValidEmail(trimmedEmail));
        } else {
            setIsEmailInvalid(false);
        }
    }, [newSignatory.email]);

    const handleCreateSignatory = async () => {
        if (!newSignatory.first_name || !newSignatory.last_name || !newSignatory.email || !newSignatory.title || !checkCompany?.id) {
            return;
        }

        if (!isValidEmail(newSignatory.email)) {
            toast("Please enter a valid email address", toastFail);
            return;
        }

        setIsCreatingSignatory(true);
        setCheckResponseError(undefined);

        try {
            const apiResponse = await apiCheckCreateSignatory(checkCompany.id, newSignatory);

            if (!apiResponse) {
                toast("Error creating signatory", toastFail);
                setIsCreatingSignatory(false);
                return;
            }

            if (isCheckError(apiResponse)) {
                toast("Error creating signatory", toastFail);
                setIsCreatingSignatory(false);
                setCheckResponseError(apiResponse.checkResponseError);
                if (onError) {
                    onError(apiResponse.checkResponseError);
                }
                return;
            }

            const createdSignatory = apiResponse.data;
                
            if (!createdSignatory) {
                toast("Error creating signatory", toastFail);
                setIsCreatingSignatory(false);
                return;
            }

            toast("Signatory created successfully", toastSuccess);

            // Refresh signatories list
            if (checkCompany?.id) {
                const apiResponse = await apiCheckGetSignatories(checkCompany.id);

                if (!apiResponse) {
                    toast("Error loading signatories", toastFail);
                    setIsCreatingSignatory(false);
                    return;
                }

                setCheckResponseError(apiResponse.checkResponseError);
                if (onError) {
                    onError(apiResponse.checkResponseError);
                }

                if (isCheckError(apiResponse)) {
                    toast("Error loading signatories", toastFail);
                    setIsCreatingSignatory(false);
                    return;
                }

                const updatedSignatories = apiResponse.data;

                if (updatedSignatories && onSignatoryCreated) {
                    onSignatoryCreated(updatedSignatories);
                }
            }

            // Reset form
            setNewSignatory({
                first_name: "",
                middle_name: "",
                last_name: "",
                email: "",
                title: "",
            });

            setIsCreatingSignatory(false);
        } catch (error) {
            console.error("[AppPaySignatoryNew] Error creating signatory:", error);
            toast("Error creating signatory", toastFail);
            setIsCreatingSignatory(false);
        }
    };

    return (
        <div className="app-pay-enroll-new-contents">
            <div className="app-pay-enroll-new-left">
                {checkResponseError && 
                    <AppCheckErrorMessage
                        checkResponseError={checkResponseError}
                    />
                }

                <Label>
                    Add Signatory
                </Label>
                <Label style={{ marginTop: "16px" }} required>
                    First Name
                </Label>
                <div className="app-custom-property-value">
                    <input 
                        type="text"
                        placeholder="Enter first name"
                        value={newSignatory.first_name}
                        onChange={(e) => {
                            setNewSignatory({ ...newSignatory, first_name: e.target.value });
                        }}
                    />
                </div>

                <Label>
                    Middle Name
                </Label>
                <div className="app-custom-property-value">
                    <input 
                        type="text"
                        placeholder="Enter middle name (optional)"
                        value={newSignatory.middle_name}
                        onChange={(e) => {
                            setNewSignatory({ ...newSignatory, middle_name: e.target.value });
                        }}
                    />
                </div>

                <Label required>
                    Last Name
                </Label>
                <div className="app-custom-property-value">
                    <input 
                        type="text"
                        placeholder="Enter last name"
                        value={newSignatory.last_name}
                        onChange={(e) => {
                            setNewSignatory({ ...newSignatory, last_name: e.target.value });
                        }}
                    />
                </div>

                <Label 
                    required
                    invalid={isEmailInvalid}
                    invalidReason="Invalid email address"
                >
                    Email
                </Label>
                <div className="app-custom-property-value">
                    <input 
                        type="email"
                        placeholder="Enter email address"
                        value={newSignatory.email}
                        onChange={(e) => {
                            setNewSignatory({ ...newSignatory, email: e.target.value });
                        }}
                    />
                </div>

                <Label required>
                    Title
                </Label>
                <div className="app-custom-property-value">
                    <input 
                        type="text"
                        placeholder="Enter title"
                        value={newSignatory.title}
                        onChange={(e) => {
                            setNewSignatory({ ...newSignatory, title: e.target.value });
                        }}
                    />
                </div>

                <Button
                    label="Add Signatory"
                    onClick={handleCreateSignatory}
                    disabled={isCreatingSignatory || !newSignatory.first_name || !newSignatory.last_name || !newSignatory.email || !isValidEmail(newSignatory.email) || !newSignatory.title || !checkCompany?.id}
                />
            </div>
        </div>
    );
};

